<!-- components/common-header/MobileHeader.vue -->
<script setup lang="ts">
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useAssetsImgs } from "@/hook/useAssetsImgs";
import MobileMenuList from "./MobileMenuList.vue";
import type { NavigationItem, ToggleParams } from "./type";
import { useLocale } from "@/hook/useLocale";

const { localeLabel, toggleLocale, loginLabel } = useLocale();

interface Props {
  logoPath?: string;
  logoAlt?: string;
  logoHeight?: string;
  menuItems?: NavigationItem[];
  showLogoText?: boolean;
  logoText?: string;
  loginPath?: string;
  showLogin?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  logoPath: "@/assets/img/logo_column.png",
  logoAlt: "logo",
  logoHeight: "h-15",
  showLogoText: true,
  logoText: "I C A A",
  loginPath: "/login",
  showLogin: true,
  menuItems: () => [],
});

const { imageUrl } = useAssetsImgs(props.logoPath);

const emit = defineEmits<{
  (e: "menu-click", item: NavigationItem): void;
  (e: "toggle", params: ToggleParams): void;
  (e: "logo-click"): void;
}>();

const isOpen = ref(false);
const route = useRoute();

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

const closeMenu = () => {
  isOpen.value = false;
};

const handleToggle = (params: ToggleParams) => {
  emit("toggle", params);
};

const handleMenuItemClick = (item: NavigationItem) => {
  emit("menu-click", item);
  closeMenu();
};

const handleLogoClick = () => {
  emit("logo-click");
  closeMenu();
};

const handleLanguageToggle = (e: Event) => {
  e.preventDefault();
  toggleLocale();
};

// 路由变化自动关闭菜单
watch(
  () => route.fullPath,
  () => {
    closeMenu();
  }
);
</script>

<template>
  <header
    class="fixed top-0 left-0 z-50 flex w-full bg-white/50 backdrop-blur-md shadow-md xl:hidden"
  >
    <div class="flex w-full items-center justify-between px-6">
      <!-- Logo -->
      <div class="col-auto">
        <div class="header-logo">
          <router-link to="/" @click="handleLogoClick">
            <img
              :src="imageUrl"
              :class="logoHeight"
              :alt="logoAlt"
              width="44px"
              :height="logoHeight"
            />
          </router-link>
        </div>
      </div>

      <!-- 汉堡菜单按钮 -->
      <div class="menu-toggle flex h-full items-center justify-center">
        <button
          @click="toggleMenu"
          aria-label="Toggle menu"
          class="inline-block h-11.5 w-11.5 bg-[#C57642] px-3.5 text-center text-[16px] text-[#fff]"
        >
          <svg
            v-if="!isOpen"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <!-- 移动端侧边菜单 -->
    <Teleport to="body">
      <!-- 遮罩层 -->
      <transition
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          class="fixed inset-0 z-40 bg-black/90"
          @click="closeMenu"
        ></div>
      </transition>

      <!-- 抽屉菜单 -->
      <transition
        enter-active-class="transition-transform duration-300 ease-out"
        leave-active-class="transition-transform duration-200 ease-in"
        enter-from-class="-translate-x-full"
        leave-to-class="-translate-x-full"
      >
        <div
          v-if="isOpen"
          class="fixed top-0 left-0 z-50 h-full w-64 bg-white shadow-xl"
        >
          <div v-if="showLogoText" class="mobile-logo bg-[#373E43]">
            <router-link
              class="text-4xl tracking-[4px] text-white"
              to="/"
              @click="handleLogoClick"
            >
              {{ logoText }}
            </router-link>
          </div>

          <div class="mt-[30px] overflow-y-auto px-[20px]">
            <MobileMenuList
              :items="menuItems"
              accordion
              @toggle="handleToggle"
              @item-click="handleMenuItemClick"
            />
          </div>

          <!-- 底部菜单 -->
          <div class="mobile-menu-footer flex flex-col pl-[60px] pt-[20px]">
            <button @click="handleLanguageToggle" class="mobile-action w-25">
              {{ localeLabel }}
            </button>
            <button @click="closeMenu" class="mobile-action w-25">
              {{ loginLabel }}
            </button>
          </div>
        </div>
      </transition>
    </Teleport>
  </header>
</template>

<style scoped>
.header-logo {
  padding-top: 10px;
  padding-bottom: 10px;
}

.mobile-logo {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  text-align: center;
}

.mobile-menu-footer {
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 15px;
  align-items: flex-start;
  justify-content: flex-start;
}

.mobile-action {
  min-height: 40px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: #171512;
  cursor: pointer;
}

.mobile-menu-footer a {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 4px;
  transition: all 0.3s ease;
  text-decoration: none;
  color: var(--title-color, #373e43);
}

.mobile-menu-footer a:last-child {
  background: var(--theme-color, #c57642);
  color: white;
}

.mobile-menu-footer a:last-child:hover {
  opacity: 0.9;
}
</style>
